import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, Eye, ChevronDown, ChevronUp, Building2, Grid, List, Package } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { getEquipmentStatus, getStatusColor, getStatusLabel, formatDate, renderCell } from '../../utils/helpers';
import { EQUIPMENT_ICONS, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';

const EquipmentList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { siteId } = useParams();
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [expandedSites, setExpandedSites] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'card' or 'table' - default to table
  const cardViewRef = useRef(null);

  useEffect(() => {
    loadSitesAndEquipment();
  }, []);

  const loadSitesAndEquipment = async () => {
    try {
      setLoading(true);
      // Load sites
      const sitesData = await apiService.getSitesByClient(user?.id);
      setSites(sitesData);
      
      // Load equipment
      const equipmentData = await apiService.getEquipment(user?.id);
      setEquipment(equipmentData);
      
      // If siteId is provided, expand only that site
      if (siteId) {
        setExpandedSites({ [siteId]: true });
      } else {
        // By default, expand all sites
        const allExpanded = {};
        sitesData.forEach(site => {
          allExpanded[site.id] = true;
        });
        setExpandedSites(allExpanded);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  const getEquipmentBySite = (siteId) => {
    const siteEquipment = equipment.filter(eq => eq.siteId === siteId);
    if (!searchQuery) return siteEquipment;
    
    const query = searchQuery.toLowerCase();
    return siteEquipment.filter(
      (item) =>
        item.serialNumber?.toLowerCase().includes(query) ||
        item.type?.toLowerCase().includes(query) ||
        item.location?.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
            Mes Sites
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Consultez vos sites et leurs équipements
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline'}
            size="sm"
            icon={Grid}
            onClick={() => setViewMode('card')}
          >
            <span className="hidden sm:inline">Cartes</span>
          </Button>
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            size="sm"
            icon={List}
            onClick={() => setViewMode('table')}
          >
            <span className="hidden sm:inline">Tableau</span>
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <Input
          placeholder="Rechercher un équipement..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Adresse
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Équipements
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.map((site, index) => {
                  const siteEquipment = getEquipmentBySite(site.id);
                  return (
                    <motion.tr
                      key={site.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-red-100 rounded-lg">
                            <MapPin className="w-4 h-4 text-red-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{site.name}</p>
                            <p className="text-xs text-gray-500">ID: {site.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {renderCell(site.address)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {siteEquipment.length}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="primary"
                          size="sm"
                          icon={Eye}
                          onClick={() => {
                            // Toggle expand for this site and switch to card view
                            setExpandedSites({ [site.id]: true });
                            setViewMode('card');
                            // Scroll to card view after a short delay
                            setTimeout(() => {
                              cardViewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }}
                        >
                          Voir les équipements
                        </Button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {sites.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucun site trouvé
              </h3>
              <p className="text-gray-600">
                Aucun site enregistré pour le moment
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Card View - Sites with Equipment */}
      {viewMode === 'card' && (
      <div ref={cardViewRef} className="space-y-4">
        {sites.map((site, siteIndex) => {
          const siteEquipment = getEquipmentBySite(site.id);
          const isExpanded = expandedSites[site.id];
          
          return (
            <motion.div
              key={site.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: siteIndex * 0.1 }}
            >
              <Card className="overflow-hidden">
                {/* Site Header */}
                <div
                  className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSite(site.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-100 rounded-lg">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{site.name}</h3>
                      <p className="text-sm text-gray-600">{site.address || 'Adresse non spécifiée'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {siteEquipment.length} équipement{siteEquipment.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600">
                      {isExpanded ? 'Masquer' : 'Voir les équipements'}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                </div>

                {/* Equipment List */}
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-200"
                  >
                    <div className="p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {siteEquipment.map((item, index) => {
                          const status = getEquipmentStatus(item);
                          return (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <div
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => navigate(`/my-equipment/${item.id}`)}
                              >
                                <div className="space-y-3">
                                  {/* Icon and Type */}
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2">
                                      <div className="text-3xl">{EQUIPMENT_ICONS[item.type]}</div>
                                      <div>
                                        <h4 className="font-semibold text-gray-900 text-sm">
                                          {EQUIPMENT_TYPE_LABELS[item.type] || item.type}
                                        </h4>
                                        <p className="text-xs text-gray-500">{item.serialNumber}</p>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                      {getStatusLabel(status)}
                                    </span>
                                  </div>

                                  {/* Details */}
                                  <div className="space-y-1 text-xs">
                                    {item.location && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-600">Emplacement:</span>
                                        <span className="font-medium text-gray-900">{item.location}</span>
                                      </div>
                                    )}
                                    {item.nextMaintenanceDate && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-600">Prochaine vérification:</span>
                                        <span className="font-medium text-gray-900">
                                          {formatDate(item.nextMaintenanceDate)}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Action */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full text-xs"
                                    icon={Eye}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/my-equipment/${item.id}`);
                                    }}
                                  >
                                    Détails
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      
                      {siteEquipment.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          Aucun équipement dans ce site
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>
      )}

      {/* Empty State for Card View */}
      {viewMode === 'card' && sites.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun site trouvé
            </h3>
            <p className="text-gray-600">
              Aucun site enregistré pour le moment
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default EquipmentList;

